import { PublicClientApplication } from "@azure/msal-browser";
import {
	usePortalModeStore,
	type PortalMode,
} from "@stores/usePortalModeStore";

const staffPortalClientId = import.meta.env.VITE_STAFF_PORTAL_CLIENT_ID;
const studentPortalClientId = import.meta.env.VITE_STUDENT_PORTAL_CLIENT_ID;
const parentPortalClientId = import.meta.env.VITE_PARENT_PORTAL_CLIENT_ID;
const internalTenantId = import.meta.env.VITE_AZURE_TENANT_ID;
const externalTenantId = import.meta.env.VITE_AZURE_EXTERNAL_TENANT_ID;
const externalAuthorityHost =
	import.meta.env.VITE_AZURE_EXTERNAL_AUTHORITY_HOST ??
	"login.parent.godalming.ac.uk";

export function getPortalClientId(
	hostMode: PortalMode = usePortalModeStore.getState().hostMode,
) {
	if (hostMode === "staff") return staffPortalClientId;
	if (hostMode === "parent") return parentPortalClientId;
	return studentPortalClientId;
}

export function getPortalAuthority(
	hostMode: PortalMode = usePortalModeStore.getState().hostMode,
) {
	if (hostMode === "parent") {
		return `https://${externalAuthorityHost}/${externalTenantId}`;
	}

	return `https://login.microsoftonline.com/${internalTenantId}`;
}

export function getApiClientId(
	hostMode: PortalMode = usePortalModeStore.getState().hostMode,
) {
	return hostMode === "parent"
		? import.meta.env.VITE_PARENT_PORTAL_API_CLIENT_ID
		: import.meta.env.VITE_AZURE_API_CLIENT_ID;
}

export function getApiTokenRequest(
	hostMode: PortalMode = usePortalModeStore.getState().hostMode,
) {
	const resource = `api://portalapi.godalming.ac.uk/${getApiClientId(hostMode)}`;

	return {
		scopes: [
			hostMode === "parent"
				? `${resource}/Portal.Use`
				: `${resource}/access_as_user`,
		],
	};
}
export const msalConfig = {
	auth: {
		clientId: getPortalClientId(),
		authority: getPortalAuthority(),
		redirectUri: window.location.origin,
		postLogoutRedirectUri: "/sign-in",
		// The staff portal can start authentication before the router renders,
		// so let MSAL restore deep links after handling the redirect response.
		navigateToLoginRequestUrl: true,
		knownAuthorities: [
			usePortalModeStore.getState().hostMode === "parent"
				? externalAuthorityHost
				: "",
		],
	},
	cache: {
		cacheLocation: "sessionStorage", // Configures cache location. "sessionStorage" is more secure, but "localStorage" gives you SSO between tabs.
		storeAuthStateInCookie: false, // Set this to "true" if you are having issues on IE11 or Edge
	},
	// system: {
	//     loggerOptions: {
	//         loggerCallback: (level: any, message: any, containsPii: any) => {
	//             if (containsPii) {
	//                 return;
	//             }
	//             switch (level) {
	//                 case LogLevel.Error:
	//                     console.error(message);
	//                     return;
	//                 case LogLevel.Info:
	//                     console.info(message);
	//                     return;
	//                 case LogLevel.Verbose:
	//                     console.debug(message);
	//                     return;
	//                 case LogLevel.Warning:
	//                     console.warn(message);
	//                     return;
	//                 default:
	//                     return;
	//             }
	//         },
	//     },
	// },
};

export const sharedTokenScopes = [
	"User.Read",
	"User.RevokeSessions.All",
	"User-PasswordProfile.ReadWrite.All",
];

export const staffGraphTokenRequest = {
	scopes: [
		"Calendars.Read.Shared",
		"Channel.ReadBasic.All",
		"ChannelMessage.Edit",
		"ChannelMessage.ReadWrite",
		"ChannelMessage.Send",
		"Mail.ReadWrite.Shared",
		"Team.ReadBasic.All",
		"User.ReadBasic.All",
		...sharedTokenScopes,
	],
};

export const studentGraphTokenRequest = {
	scopes: [
		"Calendars.Read.Shared",
		"ChannelMessage.Read.All",
		"Mail.ReadWrite",
		...sharedTokenScopes,
	],
};

export const parentGraphTokenRequest = {
	scopes: [...sharedTokenScopes],
};

export const loginRequest = {
	scopes:
		usePortalModeStore.getState().hostMode === "parent"
			? parentGraphTokenRequest.scopes
			: getApiTokenRequest().scopes,
	prompt:
		usePortalModeStore.getState().hostMode === "parent"
			? "login"
			: "select_account",
};

export const registerRequest = {
	scopes: ["openid", "profile", "offline_access"],
	prompt: "create",
};

export const sharepointTokenRequest = {
	scopes: ["https://godalmingacuk.sharepoint.com/.default"],
};

export const apiTokenRequest = {
	scopes: getApiTokenRequest().scopes,
};

/**
 * An optional silentRequest object can be used to achieve silent SSO
 * between applications by providing a "login_hint" property.
 */
// export const silentRequest = {
//     scopes: ["openid", "profile"],
//     loginHint: "example@domain.net"
// };

//Create MSAL instance
export const msalInstance = new PublicClientApplication(msalConfig);
